import React, { useState, useEffect } from 'react';
import './Gallery.css';

const Gallery = ({ t }) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedImage, setSelectedImage] = useState(null);
  const [allImages, setAllImages] = useState([]);
  const [categories, setCategories] = useState([]);

  // Charger dynamiquement toutes les images organisées par dossiers
  useEffect(() => {
    const loadImages = () => {
      try {
        // Fonction pour charger les images d'un dossier spécifique
        const loadFolderImages = (folderName) => {
          try {
            const imageContext = require.context('../../../public/images/gallery', true, /\.(jpg|jpeg|png|gif|webp)$/);
            const imagePaths = imageContext.keys();

            // Filtrer les images du dossier spécifique
            const folderImages = imagePaths
              .filter(path => path.startsWith(`./${folderName}/`))
              .map((path) => {
                const fileName = path.replace(`./${folderName}/`, '');
                return {
                  src: `/images/gallery/${folderName}/${fileName}`,
                  alt: folderName
                };
              });

            return folderImages;
          } catch (error) {
            return [];
          }
        };

        // Essayer de charger les images de différents dossiers
        const imageContext = require.context('../../../public/images/gallery', true, /\.(jpg|jpeg|png|gif|webp)$/);
        const allPaths = imageContext.keys();

        // Extraire les noms de dossiers uniques
        const folderNames = [...new Set(
          allPaths
            .filter(path => path.includes('/'))
            .map(path => path.split('/')[1])
        )];

        let loadedImages = [];
        let loadedCategories = [];

        if (folderNames.length > 0) {
          // Charger les images organisées par dossiers
          folderNames.forEach((folderName) => {
            const folderImages = loadFolderImages(folderName);
            if (folderImages.length > 0) {
              loadedCategories.push(folderName);
              folderImages.forEach((img) => {
                loadedImages.push({
                  id: loadedImages.length + 1,
                  ...img,
                  category: folderName
                });
              });
            }
          });
        } else {
          // Fallback: charger toutes les images du dossier racine
          const rootImages = allPaths
            .filter(path => !path.includes('/') || path.split('/').length === 2)
            .map((path, index) => ({
              id: index + 1,
              src: `/images/gallery/${path.replace('./', '')}`,
              alt: 'Galerie',
              category: 'Galerie'
            }));
          loadedImages = rootImages;
          loadedCategories = ['Galerie'];
        }

        setAllImages(loadedImages);
        setCategories(loadedCategories);
      } catch (error) {
        console.error('Error loading gallery images:', error);
        // Fallback to default images
        setAllImages([
          { id: 1, src: '/images/goree.jpg', alt: 'Galerie', category: 'Galerie' },
          { id: 2, src: '/images/lacrose.jpg', alt: 'Galerie', category: 'Galerie' }
        ]);
        setCategories(['Galerie']);
      }
    };

    loadImages();
  }, []);

  const imagesPerPage = 6;
  const totalPages = Math.ceil(allImages.length / imagesPerPage);

  const getCurrentImages = () => {
    const start = currentPage * imagesPerPage;
    const end = start + imagesPerPage;
    return allImages.slice(start, end);
  };

  const nextPage = () => {
    setCurrentPage((prev) => (prev + 1) % totalPages);
  };

  const prevPage = () => {
    setCurrentPage((prev) => (prev - 1 + totalPages) % totalPages);
  };

  const goToPage = (page) => {
    setCurrentPage(page);
  };

  const openLightbox = (image) => {
    setSelectedImage(image);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setSelectedImage(null);
    document.body.style.overflow = 'auto';
  };

  const nextImage = () => {
    const currentIndex = allImages.findIndex(img => img.id === selectedImage.id);
    const nextIndex = (currentIndex + 1) % allImages.length;
    setSelectedImage(allImages[nextIndex]);
  };

  const prevImage = () => {
    const currentIndex = allImages.findIndex(img => img.id === selectedImage.id);
    const prevIndex = (currentIndex - 1 + allImages.length) % allImages.length;
    setSelectedImage(allImages[prevIndex]);
  };

  return (
    <section id="gallery" className="gallery">
      <div className="container">
        <div className="gallery-header">
          <div className="header-content">
            <span className="section-tag">{t.tag}</span>
            <h2 className="gallery-title">{t.title}</h2>
          </div>
          <div className="gallery-controls">
            <button onClick={prevPage} className="gallery-nav-btn">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2"/>
              </svg>
            </button>
            <div className="gallery-pagination">
              {Array.from({ length: totalPages }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToPage(index)}
                  className={`page-dot ${index === currentPage ? 'active' : ''}`}
                />
              ))}
            </div>
            <button onClick={nextPage} className="gallery-nav-btn">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2"/>
              </svg>
            </button>
          </div>
        </div>

        <div className="gallery-grid">
          {getCurrentImages().map((image, index) => (
            <div
              key={image.id}
              className={`gallery-item item-${index + 1}`}
              onClick={() => openLightbox(image)}
            >
              <img src={`${process.env.PUBLIC_URL}${image.src}`} alt={image.alt} />
              <div className="gallery-overlay">
                <div className="overlay-content">
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
                    <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2"/>
                    <path d="M21 21l-4.35-4.35" stroke="currentColor" strokeWidth="2"/>
                    <path d="M11 8v6M8 11h6" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                  <p className="image-category">{image.category || image.alt}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="gallery-counter">
          <span>{String(currentPage + 1).padStart(2, '0')} / {String(totalPages).padStart(2, '0')}</span>
        </div>
      </div>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div className="lightbox" onClick={closeLightbox}>
          <button className="lightbox-close" onClick={closeLightbox}>
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2"/>
            </svg>
          </button>

          <button className="lightbox-prev" onClick={(e) => { e.stopPropagation(); prevImage(); }}>
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none">
              <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="3"/>
            </svg>
          </button>

          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <img src={`${process.env.PUBLIC_URL}${selectedImage.src}`} alt={selectedImage.alt} />
            <div className="lightbox-caption">{selectedImage.alt}</div>
          </div>

          <button className="lightbox-next" onClick={(e) => { e.stopPropagation(); nextImage(); }}>
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none">
              <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="3"/>
            </svg>
          </button>
        </div>
      )}
    </section>
  );
};

export default Gallery;
